// Client for dynamic platform routes: platform functions the Passio platform serves at
// /napi/tools/{slug} without a platform code change. Backend: api/core/routers/platform_routes.py.
// Everything here is admin only.

import { ApiError, TokenGetter } from './learningApi';

export type RouteStatus = 'off' | 'testing' | 'live';
export type RouteKind = 'search' | 'text' | 'vision' | 'kitchen';

export interface RouteDocs {
  summary?: string;
  description?: string;
  request_example?: Record<string, unknown>;
}

export interface PlatformRoute {
  function_id: string;
  family_id: string;
  function_name: string;
  customer_id: string;
  slug: string | null;
  status: RouteStatus;
  kind: string;
  tool: string;
  go_timeout_s: number | null;
  docs: RouteDocs;
  /** Passio customer ids that may call this route while it's Testing. */
  testers: string[];
  /** Passio customer ids that may call every Testing route (settings). */
  global_testers: string[];
  input_types: string[];
  path: string | null;
  route_key: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

export interface KindDefaults {
  kind: RouteKind;
  description: string;
  go_timeout_s: number;
  time_limit_s: number;
  default_model: string;
  models: string[];
}

export interface RouteDefaults {
  kinds: KindDefaults[];
  statuses: RouteStatus[];
  input_types: string[];
  fixed_routes: string[];
}

export interface FunctionRouteInfo {
  route: PlatformRoute | null;
  in_platform_account: boolean;
  suggested: { slug: string; kind: RouteKind };
}

export interface SetupBody {
  slug: string;
  kind: RouteKind;
  status: RouteStatus;
  docs?: RouteDocs;
  testers?: string[];
  go_timeout_s?: number | null;
  reset_reliability?: boolean;
}

export interface SetupResult {
  route: PlatformRoute;
  new_version: boolean;
  reliability_set: boolean;
  learning_set: boolean;
}

export interface RouteSettings {
  testers: string[];
  preview_accounts: string[];
}

export interface ImportResult {
  route: PlatformRoute;
  function_created: boolean;
  objects: string[];
  test_cases_created: number;
  test_links: number;
  skipped: string[];
}

export const PLATFORM_API_BASE = 'https://api.passiolife.com/v2/products';
export const SLUG_RE = /^[a-z][A-Za-z0-9]{2,47}$/;
export const STATUS_LABELS: Record<RouteStatus, { label: string; help: string; color: 'default' | 'warning' | 'success' }> = {
  off: { label: 'Off', help: 'Not served: the platform answers 404.', color: 'default' },
  testing: { label: 'Testing', help: 'Served only to tester customers. Not in the API docs; the demo site shows it to admins only.', color: 'warning' },
  live: { label: 'Live', help: 'Served to every customer and listed in the API docs.', color: 'success' },
};

/** platform_<snake_case slug>, as the backend names new platform functions. */
export const functionNameFor = (slug: string) => `platform_${slug.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase()}`;

export function platformRoutesApi(getToken: TokenGetter) {
  async function send<T>(path: string, init?: { method: 'POST' | 'PUT'; body?: unknown }): Promise<T> {
    const token = await getToken();
    const res = await fetch(`/api/v2/platform-routes${path}`, {
      method: init?.method,
      headers: { Authorization: `Bearer ${token}`, ...(init?.body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
      body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
    if (!res.ok) {
      let message = res.statusText;
      try {
        const body = await res.json();
        message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail || body);
      } catch {
        /* keep statusText */
      }
      throw new ApiError(message, res.status);
    }
    return res.json();
  }

  return {
    defaults: () => send<RouteDefaults>('/admin/defaults'),
    routes: () => send<{ platform_customer_id: string | null; routes: PlatformRoute[] }>('/admin/routes'),
    settings: () => send<RouteSettings>('/admin/settings'),
    saveSettings: (settings: RouteSettings) => send<RouteSettings>('/admin/settings', { method: 'PUT', body: settings }),
    get: (functionId: string) => send<FunctionRouteInfo>(`/admin/${functionId}`),
    setup: (functionId: string, body: SetupBody) => send<SetupResult>(`/admin/setup/${functionId}`, { method: 'POST', body }),
    update: (functionId: string, changes: Partial<Pick<PlatformRoute, 'slug' | 'status' | 'docs' | 'testers' | 'go_timeout_s'>>) =>
      send<PlatformRoute>(`/admin/${functionId}`, { method: 'PUT', body: changes }),
    exportBundle: (functionId: string) => send<Record<string, unknown>>(`/admin/${functionId}/export`),
    importBundle: (bundle: Record<string, unknown>, status: 'off' | 'testing') =>
      send<ImportResult>('/admin/import', { method: 'POST', body: { bundle, status } }),
    prompt: (functionId: string) => send<{ prompt: string }>(`/admin/${functionId}/platform-prompt`),
    clearModelCache: (names?: string[]) =>
      send<ClearModelCacheResult>('/admin/clear-model-cache', { method: 'POST', body: { names: names ?? null } }),
  };
}

export interface ClearModelCacheResult {
  /** False when EasyAI has no platform Redis for this environment (nothing was cleared). */
  configured: boolean;
  cleared: number;
  keys: string[];
  functions: string[];
}

export type PlatformRoutesApi = ReturnType<typeof platformRoutesApi>;

/** Saves JSON as a file through a temporary link. */
export function downloadJson(data: unknown, fileName: string): void {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
